import { MessageFeedback } from "../components/MessageFeedback";
import { useConversationStore } from "../store/conversationStore";
import { useConversationsStore } from "../store/conversationsStore";
import { useInteractions } from "./useInteractions";
import { useListenConversation } from "./useListenConversation";
import { FILE_UPLOAD_URL } from "@/api/fileApi";
import { useChatbox } from "@/components/chat/Chatbox";
import {
  type MessagingMessage,
  MessagingParticipantType,
} from "@/model/messaging";
import { useNewConversationChatStore } from "@/modules/conversation/ConversationChatStore";
import { getDPBaseUrl } from "@/utils/backend_endpoint";
import { MediaType, playSound, useBreakpoints } from "@deputy-ai/common";
import { useCallback } from "react";
import { map } from "rxjs";

export const useInitChatbox = () => {
  const {
    conversationUid,
    use: { conversation, hasHumanAgentHandOffed },
  } = useConversationStore();

  const {
    summarize,
    use: { myself },
    bind: { sendMessage },
  } = useConversationsStore();

  const {
    getMessageBubblePosition,
    handleLoadMessages,
    handleCreateNewSendMessage,
    handleSubmitForm,
    handleSendMessageFeedback,
    handleBatchSetMessageStatus,
  } = useInteractions();

  useListenConversation();

  const showAvatar = useCallback(
    (message: MessagingMessage) => {
      return message.participant?.externalId === myself?.externalId
        ? null
        : true;
    },
    [myself]
  );

  const handleSendMessageSuccess = useCallback(() => {
    void playSound("/sounds/message-sent.mp3");
  }, []);

  const onMessageReceived = useCallback((message: MessagingMessage) => {
    if (message.participant?.type === MessagingParticipantType.Visitor) {
      return;
    }
    void playSound("/sounds/message-received.mp3");
  }, []);

  const activeMessageFeedback = useCallback((message: MessagingMessage) => {
    return <MessageFeedback message={message} />;
  }, []);

  const conversationChatStore = useNewConversationChatStore({
    conversationUid,
    conversation,
    participants: {
      myself,
    },
    sse: {
      enabled: !hasHumanAgentHandOffed,
      baseUrl: getDPBaseUrl(),
      realm: "web_chat",
    },
    uploadFile: {
      uploadFileUrl: FILE_UPLOAD_URL,
    },
    createNewSendMessage: handleCreateNewSendMessage,
    createNewPendingMessage: () => {
      const msg = handleCreateNewSendMessage();
      return {
        ...msg,
        participant: {
          type: MessagingParticipantType.AIAgent,
        },
      };
    },
    loadMessages: handleLoadMessages,
    sendMessage: ({ message, payload }) => {
      return sendMessage(message.conversationUid, payload).pipe(
        map(({ message, taskUid }) => {
          return {
            message: message as MessagingMessage,
            taskUid,
          };
        })
      );
    },
    submitForm: handleSubmitForm,
    sendMessageFeedback: handleSendMessageFeedback,
    batchSetMessageStatus: handleBatchSetMessageStatus,
  });

  const media = useBreakpoints();
  const isMobile = media === MediaType.Mobile;
  const chatbox = useChatbox({
    conversationChatStore,
    conversation,
    participants: {
      myself,
    },
    markAsRead: true,
    input: {
      compact: isMobile,
      autoFocus: !!conversation?.uid,
    },
    bubble: {
      showAvatar,
      position: getMessageBubblePosition,
      customizedFooter: activeMessageFeedback,
    },
    feedback: {
      enabled: true,
    },
    callbacks: {
      onSendMessageSuccess: handleSendMessageSuccess,
      onMessageReceived,
    },
    summarize,
  });
  return chatbox;
};
